// src/pages/admin/CheckInPage.tsx
import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { BrowserQRCodeReader } from "@zxing/library";
import { useTheme } from "@mui/material/styles";
import api from "../../services/api";

const CAMERA_DUPLICATE_WINDOW_MS = 5000;
const CHECKIN_QUEUE_LIMIT = 1000;

type IngressoResponse = {
    message: string;
    valido: boolean;
    reutilizavel?: boolean;
    tipo?: string;
    ingresso?: {
        id: number;
        numero_ingresso: string;
        nome_ingresso: string;
        nome_pessoa?: string;
        cpf_pessoa?: string;
        presente: boolean;
    };
    pessoa?: {
        id: number;
        nome: string;
        cpf: string;
        email: string | null;
        telefone: string | null;
        cidade: string;
        estado: string;
        data_nascimento: string;
        estado_civil: string | null;
        profissao: string;
        genero: string;
        foto: string;
    };
};

type CheckInQueueItem = {
    id: string;
    at: string;
    code: string;
    status: "success" | "error";
    message: string;
    tipo?: string;
    nomePessoa?: string;
    cpfPessoa?: string;
    nomeComprador?: string;
    foto?: string;
};

const formatCpfMasked = (value?: string | null) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);

    if (digits.length !== 11) {
        return value || "-";
    }

    return digits.replace(/^(\d{3})(\d{6})(\d{2})$/, "$1.***.***-$3");
};

const formatCpfFull = (value?: string | null) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);

    if (digits.length !== 11) {
        return value || "-";
    }

    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
};

export default function CheckInPage() {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const isLandscape = useMediaQuery("(orientation: landscape)");
    const isTabletLandscape = isMdUp && isLandscape;

    const [manualMode, setManualMode] = useState(false);
    const [code, setCode] = useState("");
    const [scannedCode, setScannedCode] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [readingCode, setReadingCode] = useState<string | null>(null);

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState<"info" | "success" | "error">("info");
    const [alertMessage, setAlertMessage] = useState("");
    const [checkInQueue, setCheckInQueue] = useState<CheckInQueueItem[]>([]);
    const [visibleCpfIds, setVisibleCpfIds] = useState<Record<string, boolean>>({});
    const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
    const [previewName, setPreviewName] = useState<string>("Participante");

    const videoRef = useRef<HTMLVideoElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const codeReader = useRef<BrowserQRCodeReader | null>(null);
    const scannerControlsRef = useRef<{ stop: () => void } | null>(null);
    const activeStreamRef = useRef<MediaStream | null>(null);
    const cameraScanLockedRef = useRef(false);
    const lastCameraScansRef = useRef<Map<string, number>>(new Map());

    const stopCameraStream = () => {
        if (activeStreamRef.current) {
            activeStreamRef.current.getTracks().forEach((t) => t.stop());
            activeStreamRef.current = null;
        }

        if (videoRef.current?.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach((t) => t.stop());
            videoRef.current.srcObject = null;
        }
    };

    const stopCameraResources = () => {
        scannerControlsRef.current?.stop();
        scannerControlsRef.current = null;
        codeReader.current?.reset();
        stopCameraStream();
    };

    const unlockCameraFlow = () => {
        cameraScanLockedRef.current = false;
        setScannedCode(null);
    };

    const isDuplicateCameraScan = (value: string) => {
        const now = Date.now();
        const map = lastCameraScansRef.current;

        // Limpa entradas antigas para manter o controle apenas na janela curta.
        map.forEach((ts, codeItem) => {
            if (now - ts > CAMERA_DUPLICATE_WINDOW_MS) {
                map.delete(codeItem);
            }
        });

        const lastSeen = map.get(value);
        if (lastSeen && now - lastSeen <= CAMERA_DUPLICATE_WINDOW_MS) {
            return true;
        }

        map.set(value, now);
        return false;
    };

    const pushQueueItem = (item: Omit<CheckInQueueItem, "id" | "at">) => {
        const queueItem: CheckInQueueItem = {
            ...item,
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            at: new Date().toLocaleTimeString("pt-BR"),
        };

        setCheckInQueue((prev) => [queueItem, ...prev].slice(0, CHECKIN_QUEUE_LIMIT));
    };

    const hasReachedQueueLimit = () => checkInQueue.length >= CHECKIN_QUEUE_LIMIT;

    const handleOpenPreview = (foto: string, nome?: string) => {
        setPreviewPhoto(foto);
        setPreviewName(nome || "Participante");
    };

    const handleClosePreview = () => {
        setPreviewPhoto(null);
    };

    const resetAll = () => {
        setCode("");
        unlockCameraFlow();
        stopCameraResources();
    };

    const checkIn = async (qr: string, source: "camera" | "manual") => {
        setLoading(true);
        try {
            const res = await api.post<IngressoResponse>("/auth/checkin", {
                numero_ingresso_qr: qr,
            });
            const data = res.data;
            const queueItemBase = {
                code: data.ingresso?.numero_ingresso || qr,
                tipo: data.tipo,
                nomePessoa: data.ingresso?.nome_pessoa || data.pessoa?.nome,
                cpfPessoa: data.ingresso?.cpf_pessoa || data.pessoa?.cpf,
                nomeComprador: data.pessoa?.nome,
                foto: data.pessoa?.foto,
            };

            if (data.valido) {
                setAlertSeverity("success");
                setAlertMessage(data.message);
                pushQueueItem({
                    ...queueItemBase,
                    status: "success",
                    message: data.message,
                });
            } else {
                setAlertSeverity("error");
                setAlertMessage(data.message);
                pushQueueItem({
                    ...queueItemBase,
                    status: "error",
                    message: data.message,
                });
            }

            setAlertOpen(true);
        } catch (err: any) {
            const msg =
                err.response?.data?.message || "Erro ao realizar o check‑in.";
            setAlertSeverity("error");
            setAlertMessage(msg);
            pushQueueItem({
                code: qr,
                status: "error",
                message: msg,
            });
            setAlertOpen(true);
        } finally {
            setLoading(false);
            setReadingCode(null);
            if (source === "camera") {
                unlockCameraFlow();
            }
        }
    };

    const handleScanned = async (text: string, source: "camera" | "manual") => {
        if (hasReachedQueueLimit()) {
            setAlertSeverity("error");
            setAlertMessage(`Fila de check-in cheia (${CHECKIN_QUEUE_LIMIT}). Novas entradas foram bloqueadas.`);
            setAlertOpen(true);
            if (source === "camera") {
                cameraScanLockedRef.current = true;
                stopCameraResources();
            }
            return;
        }

        if (source === "camera") {
            if (cameraScanLockedRef.current) return;
            cameraScanLockedRef.current = true;
            setScannedCode(text);
            stopCameraResources();
        }

        setReadingCode(text);
        await checkIn(text, source);
    };

    useEffect(() => {
        // monta o decoder de QR apenas se não em modo manual e não já escaneado
        if (manualMode || scannedCode !== null) return;

        let cancelled = false;
        let captureStreamTimer: number | null = null;
        const reader = new BrowserQRCodeReader();
        codeReader.current = reader;

        reader
            .decodeFromVideoDevice(
                null,
                videoRef.current!,
                (result, err) => {
                    if (result && !cameraScanLockedRef.current) {
                        const qrText = result.getText().trim().toUpperCase();
                        if (isDuplicateCameraScan(qrText)) {
                            return;
                        }
                        handleScanned(qrText, "camera");
                    }
                }
            )
            .then((controls: any) => {
                if (cancelled) {
                    controls?.stop?.();
                    return;
                }
                scannerControlsRef.current = controls;
                // Guarda o stream ativo para conseguir desligar a câmera mesmo sem o elemento de vídeo montado.
                captureStreamTimer = window.setInterval(() => {
                    const stream = videoRef.current?.srcObject as MediaStream | null;
                    if (stream) {
                        activeStreamRef.current = stream;
                    }
                }, 120);
            })
            .catch(() => {
                // Ignora falhas transitórias de inicialização de câmera.
            });

        return () => {
            cancelled = true;
            if (captureStreamTimer) {
                window.clearInterval(captureStreamTimer);
            }
            stopCameraResources();
        };
    }, [manualMode, scannedCode]);

    // ao desmontar de fato o componente, interrompe a câmera
    useEffect(() => {
        const handleVisibility = () => {
            if (document.hidden) {
                stopCameraResources();
            }
        };

        const handlePageHide = () => {
            stopCameraResources();
        };

        document.addEventListener("visibilitychange", handleVisibility);
        window.addEventListener("pagehide", handlePageHide);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibility);
            window.removeEventListener("pagehide", handlePageHide);
            stopCameraResources();
        };
    }, []);

    const toggleManualMode = () => {
        resetAll();
        setManualMode((m) => !m);
    };

    const handleManualSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (code.trim()) {
            const manualCode = code.trim().toUpperCase();
            setCode("");
            await handleScanned(manualCode, "manual");
            inputRef.current?.focus();
        }
    };

    return (
        <Box
            minHeight="100vh"
            display="flex"
            justifyContent="center"
            alignItems="stretch"
            p={2}
            gap={2}
            flexDirection={isTabletLandscape ? "row" : "column"}
            bgcolor="background.default"
        >
            <Box
                flex={isTabletLandscape ? 1.4 : undefined}
                maxWidth={isTabletLandscape ? "none" : 600}
                width="100%"
                p={4}
                boxShadow={3}
                borderRadius={2}
                bgcolor="background.paper"
            >
                <Stack spacing={3} alignItems="center">
                    <Typography variant="h5">Check‑in de Participantes</Typography>

                    {!manualMode ? (
                        <>
                            <video
                                ref={videoRef}
                                style={{ width: "100%", borderRadius: 8 }}
                            />
                            <Button
                                onClick={toggleManualMode}
                                variant="outlined"
                                fullWidth
                                sx={{ mt: 2 }}
                                disabled={loading}
                            >
                                Digitar código
                            </Button>
                        </>
                    ) : (
                        <Box
                            component="form"
                            onSubmit={handleManualSubmit}
                            width="100%"
                        >
                            <TextField
                                inputRef={inputRef}
                                fullWidth
                                label="Código QR"
                                value={code}
                                onChange={(e) =>
                                    setCode(e.target.value.toUpperCase())
                                }
                                margin="normal"
                                inputProps={{ style: { textTransform: "uppercase" } }}
                            />
                            <Stack direction="row" spacing={2} mt={2}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        "Fazer Check‑in"
                                    )}
                                </Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={toggleManualMode}
                                    disabled={loading}
                                >
                                    Voltar
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </Box>

            <Box
                flex={isTabletLandscape ? 1 : undefined}
                width="100%"
                maxWidth={isTabletLandscape ? 380 : 420}
                p={3}
                boxShadow={3}
                borderRadius={2}
                bgcolor="background.paper"
            >
                <Stack spacing={2}>
                    <Typography variant="h6">Fila de check-ins (ultimos 1000)</Typography>
                    {loading && readingCode && (
                        <Box
                            p={1.5}
                            borderRadius={1}
                            border="2px solid"
                            borderColor="info.light"
                            bgcolor="info.50"
                            boxShadow={1}
                        >
                            <Stack direction="row" spacing={1.25} alignItems="center">
                                <CircularProgress size={20} />
                                <Box>
                                    <Typography variant="body2" fontWeight={700}>
                                        Lendo ingresso. Aguarde...
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Numero do ingresso: {readingCode}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}
                    {checkInQueue.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Nenhum check-in registrado nesta sessao.
                        </Typography>
                    ) : (
                        checkInQueue.map((item, index) => (
                            <Box
                                key={item.id}
                                p={1.5}
                                borderRadius={1}
                                border={index === 0 ? "3px solid" : "1px solid"}
                                borderColor={
                                    index === 0
                                        ? "grey.500"
                                        : item.status === "success"
                                            ? "success.light"
                                            : "error.light"
                                }
                                bgcolor={index === 0 ? "grey.100" : item.status === "success" ? "success.50" : "error.50"}
                                boxShadow={index === 0 ? 2 : 0}
                            >
                                {index === 0 && item.foto && (
                                    <Box mb={1} display="flex" justifyContent="center">
                                        <Avatar
                                            src={item.foto}
                                            alt={item.nomePessoa || "Participante"}
                                            onClick={() => handleOpenPreview(item.foto!, item.nomePessoa)}
                                            sx={{
                                                width: "50%",
                                                maxWidth: 170,
                                                height: "auto",
                                                aspectRatio: "1 / 1",
                                                cursor: "zoom-in",
                                                border: "2px solid",
                                                borderColor: "divider",
                                            }}
                                        />
                                    </Box>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    {index === 0 ? "Mais recente" : item.at}
                                </Typography>
                                <Box
                                    mt={0.75}
                                    mb={0.75}
                                    px={1.25}
                                    py={0.5}
                                    borderRadius={1}
                                    bgcolor={item.status === "success" ? "success.main" : "error.main"}
                                    display="flex"
                                    justifyContent="center"
                                >
                                    <Typography
                                        variant="body1"
                                        fontWeight={800}
                                        textTransform="uppercase"
                                        textAlign="center"
                                        color="common.white"
                                    >
                                        {item.status === "success" ? "Acesso liberado" : "Acesso negado"}
                                    </Typography>
                                </Box>
                                <Stack
                                    spacing={0.25}
                                    sx={{ cursor: item.cpfPessoa ? "pointer" : "default" }}
                                    onClick={() => {
                                        if (!item.cpfPessoa) {
                                            return;
                                        }

                                        setVisibleCpfIds((prev) => ({
                                            ...prev,
                                            [item.id]: !prev[item.id],
                                        }));
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        Titular do ingresso
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Número do ingresso: {item.code || "-"}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {item.nomePessoa || "-"}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        CPF do titular: {item.cpfPessoa ? (visibleCpfIds[item.id] ? formatCpfFull(item.cpfPessoa) : formatCpfMasked(item.cpfPessoa)) : "-"}
                                    </Typography>
                                    {item.cpfPessoa ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {visibleCpfIds[item.id] ? "Clique para ocultar o CPF" : "Clique para exibir o CPF completo"}
                                        </Typography>
                                    ) : null}
                                </Stack>
                                <Typography variant="body2">
                                    {item.message}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" display="block">
                                    Nome do comprador: {item.nomeComprador || "-"}
                                </Typography>
                                {item.tipo && (
                                    <Typography variant="caption" display="block">
                                        Tipo: {item.tipo}
                                    </Typography>
                                )}
                            </Box>
                        ))
                    )}
                </Stack>
            </Box>

            {/* Snackbar para feedback rápido (opcional) */}
            <Snackbar
                open={alertOpen}
                autoHideDuration={3000}
                onClose={() => setAlertOpen(false)}
            >
                <Alert severity={alertSeverity} sx={{ width: "100%" }}>
                    {alertMessage}
                </Alert>
            </Snackbar>

            <Dialog
                open={Boolean(previewPhoto)}
                onClose={handleClosePreview}
                maxWidth="sm"
                fullWidth
            >
                <DialogContent sx={{ p: 1.5 }}>
                    {previewPhoto && (
                        <Box
                            component="img"
                            src={previewPhoto}
                            alt={previewName}
                            onClick={handleClosePreview}
                            sx={{
                                width: "100%",
                                borderRadius: 1,
                                display: "block",
                                cursor: "zoom-out",
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
